/**
 * packages/core/src/retry.ts
 *
 * Retry 유틸리티 — exponential backoff + jitter
 *
 * 외부 API(LLM, 결제, 이메일 등) 호출 실패 시 자동 재시도.
 * ctx.retry() 또는 독립 함수 withRetry()로 사용 가능.
 *
 * @example
 * // 독립 함수
 * import { withRetry } from "@gencow/core";
 * const result = await withRetry(() => fetch("https://api.example.com"), {
 *     maxAttempts: 5,
 *     initialBackoffMs: 500,
 * });
 *
 * // ctx에 내장
 * export const charge = mutation({
 *     handler: async (ctx, args) => {
 *         const result = await ctx.retry(
 *             () => stripe.charges.create({ amount: args.amount }),
 *             { maxAttempts: 3, initialBackoffMs: 500 },
 *         );
 *         return result;
 *     },
 * });
 */

// ─── 타입 ───────────────────────────────────────────────

export interface RetryOptions {
  /** 최대 시도 횟수 (기본 3) */
  maxAttempts?: number;
  /** 첫 번째 재시도 대기 시간 (ms, 기본 1000) */
  initialBackoffMs?: number;
  /** backoff 배수 (기본 2 → 1s, 2s, 4s, 8s, ...) */
  base?: number;
  /** 최대 대기 시간 (ms, 기본 30000) */
  maxBackoffMs?: number;
  /** 재시도할 에러인지 판단 (기본: 모든 에러 재시도) */
  shouldRetry?: (error: unknown, attempt: number) => boolean;
  /** 재시도 시 호출되는 콜백 (로깅용) */
  onRetry?: (error: unknown, attempt: number, delayMs: number) => void;
}

// ─── 기본값 ─────────────────────────────────────────────

const DEFAULT_MAX_ATTEMPTS = 3;
const DEFAULT_INITIAL_BACKOFF_MS = 1000;
const DEFAULT_BASE = 2;
const DEFAULT_MAX_BACKOFF_MS = 30_000;
const JITTER_MIN = 0.75;
const JITTER_MAX = 1.25;

// ─── 핵심 함수 ──────────────────────────────────────────

/**
 * 재시도 가능한 비동기 함수 실행
 *
 * Exponential backoff + jitter로 재시도.
 * delay = min(initialBackoffMs × base^attempt × jitter, maxBackoffMs)
 */
export async function withRetry<T>(fn: () => Promise<T>, options: RetryOptions = {}): Promise<T> {
  const {
    maxAttempts = DEFAULT_MAX_ATTEMPTS,
    initialBackoffMs = DEFAULT_INITIAL_BACKOFF_MS,
    base = DEFAULT_BASE,
    maxBackoffMs = DEFAULT_MAX_BACKOFF_MS,
    shouldRetry,
    onRetry,
  } = options;

  if (maxAttempts < 1) {
    throw new Error(`withRetry: maxAttempts must be >= 1, got ${maxAttempts}`);
  }

  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (error: unknown) {
      const isLastAttempt = attempt === maxAttempts - 1;

      // 마지막 시도면 에러 throw
      if (isLastAttempt) throw error;

      // shouldRetry가 있으면 판단
      if (shouldRetry && !shouldRetry(error, attempt)) throw error;

      // 딜레이 계산: exponential backoff + jitter
      const jitter = JITTER_MIN + Math.random() * (JITTER_MAX - JITTER_MIN);
      const rawDelay = initialBackoffMs * Math.pow(base, attempt) * jitter;
      const delay = Math.min(rawDelay, maxBackoffMs);

      // 콜백 호출
      if (onRetry) onRetry(error, attempt + 1, delay);

      await sleep(delay);
    }
  }

  // TypeScript를 위한 unreachable — 실제로 여기에 도달하지 않음
  throw new Error("withRetry: unreachable");
}

// ─── 내부 유틸리티 ──────────────────────────────────────

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}
